import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Lock, Settings2, ShieldAlert, Save, Pencil, Trash2, Play } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { RupiahInput } from "@/components/RupiahInput";
import { useFinance } from "@/context/FinanceContext";
import { simulateStress, simulatorBase } from "@/lib/simulator";
import { formatCompact } from "@/lib/currency";

const STATUS = {
  sehat: "bg-emerald-100 text-emerald-700",
  waspada: "bg-amber-100 text-amber-700",
  perhatian: "bg-rose-100 text-rose-700",
};
const COMPARE_LABEL = { sehat: "Aman", waspada: "Waspada", perhatian: "Kritis" };
const COMPARE_TEXT = { sehat: "text-emerald-600", waspada: "text-amber-600", perhatian: "text-rose-600" };
const sliderStyle =
  "mt-3 [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&>span:first-child_span]:bg-zinc-900 [&_[role=slider]]:border-zinc-900";

function runwayText(m) {
  if (!isFinite(m)) return "Cashflow positif";
  if (m >= 24) return "24+ bln";
  return `${m.toFixed(1).replace(".", ",")} bln`;
}
function signed(v) {
  if (!v) return "Rp0";
  return (v > 0 ? "+" : "-") + formatCompact(Math.abs(v));
}

export default function Simulator() {
  const navigate = useNavigate();
  const { finance, upsertScenario, deleteScenario } = useFinance();
  const base = useMemo(() => simulatorBase(finance), [finance]);
  const scenarios = finance?.scenarios || [];

  const [incomeDrop, setIncomeDrop] = useState(0); // %
  const [expenseUp, setExpenseUp] = useState(0); // %
  const [oneTime, setOneTime] = useState(0);
  const [newDebt, setNewDebt] = useState(0);
  const [lostSourceId, setLostSourceId] = useState("");
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const params = {
    incomeDropPct: incomeDrop / 100,
    expenseUpPct: expenseUp / 100,
    oneTime,
    newDebtMonthly: newDebt,
    lostSourceId: lostSourceId || null,
  };

  const sim = useMemo(
    () => simulateStress(finance, params),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [finance, incomeDrop, expenseUp, oneTime, newDebt, lostSourceId]
  );

  const reset = () => {
    setIncomeDrop(0); setExpenseUp(0); setOneTime(0); setNewDebt(0); setLostSourceId(""); setEditingId(null); setName("");
  };
  const saveScenario = () => {
    const nm = name.trim() || `Skenario ${scenarios.length + 1}`;
    upsertScenario(editingId ? { id: editingId, name: nm, params } : { name: nm, params });
    toast.success(editingId ? "Skenario diperbarui" : "Skenario disimpan");
    setEditingId(null); setName("");
  };
  const loadParams = (p) => {
    setIncomeDrop(Math.round((p.incomeDropPct || 0) * 100));
    setExpenseUp(Math.round((p.expenseUpPct || 0) * 100));
    setOneTime(p.oneTime || 0);
    setNewDebt(p.newDebtMonthly || 0);
    setLostSourceId(p.lostSourceId || "");
  };
  const openScenario = (sc) => { loadParams(sc.params); setEditingId(null); setName(""); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const editScenario = (sc) => { loadParams(sc.params); setEditingId(sc.id); setName(sc.name); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const removeScenario = (id) => { deleteScenario(id); setSelectedIds((s) => s.filter((x) => x !== id)); };
  const toggleCompare = (id) => {
    setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length >= 3 ? s : [...s, id]));
  };

  const baselineOutflow = base.committed + base.monthlyVariable;
  const compareCols = [
    { id: "__base", name: "Kondisi Saat Ini", sim: simulateStress(finance, {}), newDebt: 0, oneTime: 0 },
    ...selectedIds
      .map((id) => scenarios.find((s) => s.id === id))
      .filter(Boolean)
      .map((sc) => ({ id: sc.id, name: sc.name, sim: simulateStress(finance, sc.params), newDebt: sc.params.newDebtMonthly || 0, oneTime: sc.params.oneTime || 0 })),
  ];

  const Header = (
    <header className="mb-5 flex items-center gap-3">
      <button data-testid="sim-back" onClick={() => navigate("/app/catatan?tab=analisis")} className="text-zinc-500">
        <ArrowLeft className="h-5 w-5" />
      </button>
      <div>
        <h1 className="font-heading text-2xl font-extrabold text-zinc-900">Simulasi "Bagaimana Jika"</h1>
        <p className="text-xs text-zinc-400">Berdasarkan data pencatatan Anda · terpisah dari Stress Test Ketahanan</p>
      </div>
    </header>
  );

  if (!base.hasIncome) {
    return (
      <div className="px-6 pb-28 pt-8" data-testid="simulator-page">
        {Header}
        <div className="rounded-3xl border border-zinc-200 bg-white p-6 text-center shield-shadow" data-testid="sim-need-setup">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-100">
            <ShieldAlert className="h-6 w-6 text-zinc-400" />
          </div>
          <p className="mt-3 font-heading text-base font-bold text-zinc-900">Perlu data penghasilan</p>
          <p className="mt-1 text-sm text-zinc-500">Atur penghasilan bulanan dulu agar simulasi bisa dihitung.</p>
          <button
            data-testid="sim-open-setup"
            onClick={() => navigate("/app/keuangan")}
            className="mt-5 inline-flex h-11 items-center gap-2 rounded-full bg-zinc-900 px-5 text-sm font-semibold text-white"
          >
            <Settings2 className="h-4 w-4" /> Pengaturan Keuangan
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 pb-28 pt-8" data-testid="simulator-page">
      {Header}

      {/* Result */}
      <div className="rounded-3xl bg-zinc-900 p-6 text-white shield-shadow" data-testid="sim-result">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 font-heading">Hasil Simulasi</p>
          <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS[sim.status.level]}`} data-testid="sim-status">
            {sim.status.label}
          </span>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-4">
          <div>
            <p className="text-[11px] text-zinc-400">Surplus / bulan</p>
            <p className={`font-mono text-2xl font-black ${sim.monthlyBalance >= 0 ? "text-emerald-400" : "text-rose-400"}`} data-testid="sim-balance">
              {sim.monthlyBalance >= 0 ? "" : "-"}{formatCompact(Math.abs(sim.monthlyBalance))}
            </p>
          </div>
          <div>
            <p className="text-[11px] text-zinc-400">Dana darurat bertahan</p>
            <p className="font-mono text-2xl font-black text-white" data-testid="sim-runway">{runwayText(sim.runwayMonths)}</p>
          </div>
        </div>
        {sim.monthlyBalance >= 0 && sim.coverageMonths < 3 && (
          <p className="mt-2 text-[11px] text-amber-300" data-testid="sim-buffer-hint">
            Cashflow positif, tapi dana darurat kurang dari 3 bulan pengeluaran.
          </p>
        )}
        <div className="mt-4 border-t border-white/10 pt-3 text-[11px] text-zinc-400" data-testid="sim-compare">
          Tanpa guncangan: surplus {sim.baseline.monthlyBalance >= 0 ? "" : "-"}{formatCompact(Math.abs(sim.baseline.monthlyBalance))} / bulan · {sim.baseline.status.label}
        </div>
      </div>

      {/* Scenarios */}
      <div className="mt-5 space-y-5 rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow" data-testid="sim-controls">
        <div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-zinc-600">Penghasilan turun</span>
            <span className="font-mono text-sm font-semibold text-rose-600">-{incomeDrop}%</span>
          </div>
          <Slider data-testid="sim-income-drop" className={sliderStyle} value={[incomeDrop]} onValueChange={(v) => setIncomeDrop(v[0])} max={100} step={5} />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-zinc-600">Pengeluaran naik</span>
            <span className="font-mono text-sm font-semibold text-rose-600">+{expenseUp}%</span>
          </div>
          <Slider data-testid="sim-expense-up" className={sliderStyle} value={[expenseUp]} onValueChange={(v) => setExpenseUp(v[0])} max={100} step={5} />
        </div>

        <div>
          <label className="mb-1 block text-sm text-zinc-600">Kehilangan sumber penghasilan</label>
          <select
            data-testid="sim-lost-source"
            value={lostSourceId}
            onChange={(e) => setLostSourceId(e.target.value)}
            className="h-11 w-full rounded-2xl border border-zinc-200 bg-white px-3 text-sm"
          >
            <option value="">Tidak ada</option>
            {base.income.map((s) => {
              const optLabel = `${s.name} (${formatCompact(s.amount)})`;
              return <option key={s.id} value={s.id}>{optLabel}</option>;
            })}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm text-zinc-600">Cicilan / utang baru per bulan</label>
          <RupiahInput testId="sim-new-debt" value={newDebt} onChange={setNewDebt} />
        </div>

        <div>
          <label className="mb-1 block text-sm text-zinc-600">Pengeluaran tak terduga (sekali)</label>
          <RupiahInput testId="sim-one-time" value={oneTime} onChange={setOneTime} hint="mis. servis mobil, biaya medis" />
        </div>

        <div className="border-t border-zinc-100 pt-4">
          <label className="mb-1 block text-sm text-zinc-600">Simpan skenario ini</label>
          <div className="flex gap-2">
            <Input
              data-testid="sim-scenario-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={editingId ? "Ubah nama skenario" : "mis. PHK suami"}
              className="h-11 rounded-2xl border-zinc-200"
            />
            <Button data-testid="sim-scenario-save" onClick={saveScenario} className="h-11 rounded-full bg-zinc-900 px-4 text-sm font-semibold">
              <Save className="mr-1.5 h-4 w-4" /> {editingId ? "Update" : "Simpan"}
            </Button>
          </div>
        </div>

        <Button data-testid="sim-reset" onClick={reset} variant="outline" className="h-10 w-full rounded-full border-zinc-300 text-sm font-semibold">
          <RefreshCw className="mr-1.5 h-4 w-4" /> Reset skenario
        </Button>
      </div>

      {/* Saved scenarios */}
      {scenarios.length > 0 && (
        <div className="mt-5 rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow" data-testid="sim-saved-list">
          <p className="mb-3 font-heading text-sm font-bold text-zinc-900">Skenario Tersimpan</p>
          <div className="space-y-2">
            {scenarios.map((sc) => {
              const selected = selectedIds.includes(sc.id);
              return (
                <div key={sc.id} className="rounded-2xl border border-zinc-100 p-3" data-testid={`sim-scenario-${sc.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="min-w-0 truncate font-heading text-sm font-bold text-zinc-800">{sc.name}</p>
                    <div className="flex shrink-0 items-center gap-1">
                      <button data-testid={`sim-open-${sc.id}`} onClick={() => openScenario(sc)} className="flex h-8 w-8 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100" aria-label="Buka">
                        <Play className="h-4 w-4" />
                      </button>
                      <button data-testid={`sim-edit-${sc.id}`} onClick={() => editScenario(sc)} className="flex h-8 w-8 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100" aria-label="Edit">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button data-testid={`sim-delete-${sc.id}`} onClick={() => removeScenario(sc.id)} className="flex h-8 w-8 items-center justify-center rounded-full text-zinc-300 hover:bg-rose-50 hover:text-rose-500" aria-label="Hapus">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <label className="mt-1 flex items-center gap-2 text-xs text-zinc-500">
                    <input
                      type="checkbox"
                      data-testid={`sim-compare-${sc.id}`}
                      checked={selected}
                      onChange={() => toggleCompare(sc.id)}
                      disabled={!selected && selectedIds.length >= 3}
                      className="h-4 w-4 rounded border-zinc-300 accent-zinc-900"
                    />
                    Bandingkan (maks 3)
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Comparison table */}
      {selectedIds.length > 0 && (
        <div className="mt-5 rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow" data-testid="sim-compare-table">
          <p className="mb-3 font-heading text-sm font-bold text-zinc-900">Perbandingan Skenario</p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-white p-2 text-left font-semibold text-zinc-400"> </th>
                  {compareCols.map((c) => (
                    <th key={c.id} className="min-w-[100px] p-2 text-left font-heading font-bold text-zinc-800">{c.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="font-mono">
                {[
                  { k: "Perubahan pemasukan", get: (c) => signed(c.sim.adjIncome - base.monthlyIncome) },
                  { k: "Perubahan pengeluaran", get: (c) => signed(c.sim.adjOutflow - baselineOutflow) },
                  { k: "Surplus / bulan", get: (c) => (c.sim.monthlyBalance >= 0 ? "" : "-") + formatCompact(Math.abs(c.sim.monthlyBalance)), cls: (c) => (c.sim.monthlyBalance >= 0 ? "text-emerald-600" : "text-rose-600") },
                  { k: "Dana darurat", get: (c) => runwayText(c.sim.runwayMonths) },
                  { k: "Sisa dana darurat", get: (c) => formatCompact(c.sim.availableEmergency), cls: (c) => (c.sim.availableEmergency < 0 ? "text-rose-600" : "text-zinc-800") },
                  { k: "Pengeluaran tak terduga", get: (c) => (c.oneTime ? "-" + formatCompact(c.oneTime) : "–"), cls: (c) => (c.oneTime ? "text-rose-600" : "text-zinc-800") },
                  { k: "Dampak cicilan", get: (c) => (c.newDebt ? "+" + formatCompact(c.newDebt) + "/bln" : "–") },
                ].map((row) => (
                  <tr key={row.k} className="border-t border-zinc-100">
                    <td className="sticky left-0 bg-white p-2 font-sans text-zinc-500">{row.k}</td>
                    {compareCols.map((c) => (
                      <td key={c.id} className={`p-2 ${row.cls ? row.cls(c) : "text-zinc-800"}`}>{row.get(c)}</td>
                    ))}
                  </tr>
                ))}
                <tr className="border-t border-zinc-100">
                  <td className="sticky left-0 bg-white p-2 font-sans text-zinc-500">Status</td>
                  {compareCols.map((c) => (
                    <td key={c.id} className={`p-2 font-sans font-bold ${COMPARE_TEXT[c.sim.status.level]}`} data-testid={`compare-status-${c.id}`}>
                      {COMPARE_LABEL[c.sim.status.level]}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-4 flex items-start gap-2 rounded-2xl bg-emerald-50 p-3">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
        <p className="text-xs leading-relaxed text-emerald-700">
          Simulasi dihitung di perangkat Anda dari data pencatatan. Tidak ada nominal yang dikirim ke server, dan Skor Ketahanan (stress test) tidak terpengaruh.
        </p>
      </div>
    </div>
  );
}
